import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import axios from 'axios';
import GeneralContext from '../../utils/generalContext';
import './pdfReader.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const samplePdf = process.env.PUBLIC_URL + '/pdfs/VERBLIST1.pdf';

// Simple page component - NO drawing canvas inside (avoids react-pageflip DOM conflicts)
const PageComponent = React.forwardRef(({ pageNumber, width }, ref) => {
    return (
        <div ref={ref} className="page-wrapper" onContextMenu={(e) => e.preventDefault()}>
            <div className="pdf-layer">
                <Page
                    pageNumber={pageNumber}
                    width={width}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="pdf-page"
                />
            </div>
        </div>
    );
});

export const PDFViewer = ({ pdfUrl, itemAlumnoId }) => {
    const { user } = useContext(GeneralContext);

    // Parse query parameters as fallback for standalone iframe embedding
    let queryPdfUrl = null;
    let queryItemAlumnoId = null;
    try {
        const urlParams = new URLSearchParams(window.location.search);
        queryPdfUrl = urlParams.get('url');
        queryItemAlumnoId = urlParams.get('itemAlumnoId');
    } catch (e) {
        // gracefully handle URL parsing errors
    }

    // Use the passed pdfUrl if provided, otherwise check query params, otherwise fall back to local sample
    const activePdf = pdfUrl || queryPdfUrl || samplePdf;
    const activeItemAlumnoId = itemAlumnoId || queryItemAlumnoId;

    // Parse initial page from URL (?page=X or #page=X)
    let initialP = 0;
    try {
        if (activePdf) {
            // Use window.location.origin as base in case activePdf is a relative path
            const urlObj = new URL(activePdf, window.location.origin);
            const queryPage = urlObj.searchParams.get('page');
            let hashPage = null;
            if (urlObj.hash) {
                const match = urlObj.hash.match(/page=(\d+)/);
                if (match) hashPage = match[1];
            }
            const pageStr = hashPage || queryPage;
            if (pageStr) {
                const parsed = parseInt(pageStr, 10);
                if (!isNaN(parsed) && parsed > 0) {
                    initialP = parsed - 1; // 0-indexed for startPage
                }
            }
        }
    } catch (e) { }

    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(initialP + 1);
    const [initialPage] = useState(initialP);
    const [pdfError, setPdfError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Drawing states
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [brushColor, setBrushColor] = useState('#e63946');
    const [brushRadius, setBrushRadius] = useState(3);
    const [isHighlighter, setIsHighlighter] = useState(false);

    // Zoom
    const [scale, setScale] = useState(1);

    // Refs
    const flipBookRef = useRef(null);
    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const overlayCanvasRef = useRef(null); // THE drawing canvas - sits outside react-pageflip
    const [bookSize, setBookSize] = useState({ width: 550, height: 712 });

    // ── Annotation helpers (only active when itemAlumnoId is provided) ───────
    const getAuthHeader = () => {
        const token = localStorage.getItem('QuentliToken') || '';
        return { Authorization: `Bearer ${token}` };
    };

    const apiBase = useContext(GeneralContext)?.global?.domain || '';

    // Save current page's drawing paths to the backend
    const saveAnnotationsForPage = useCallback(async (pagina) => {
        if (!activeItemAlumnoId || !overlayCanvasRef.current) return;
        try {
            const paths = await overlayCanvasRef.current.exportPaths();
            if (!paths || paths.length === 0) return; // nothing to save
            await axios.post(
                `${apiBase}api/cursosPdfAnotaciones/guardar`,
                { itemalumnoid: activeItemAlumnoId, pagina, trazos: JSON.stringify(paths) },
                { headers: getAuthHeader() }
            );
        } catch (err) {
            console.warn('PDF: no se pudieron guardar las anotaciones', err);
        }
    }, [activeItemAlumnoId, apiBase]);

    // Load all saved paths from backend into a ref map { page: paths[] }
    const savedPathsRef = useRef({});
    useEffect(() => {
        if (!activeItemAlumnoId) return;
        axios.get(
            `${apiBase}api/cursosPdfAnotaciones/cargar/${activeItemAlumnoId}`,
            { headers: getAuthHeader() }
        ).then(({ data }) => {
            savedPathsRef.current = data || {};
        }).catch(err => console.warn('PDF: no se pudieron cargar las anotaciones', err));
    }, [activeItemAlumnoId]);

    // Load paths for a specific page onto the canvas
    const loadAnnotationsForPage = useCallback(async (pagina) => {
        if (!overlayCanvasRef.current) return;
        const paths = savedPathsRef.current[pagina];
        if (paths && paths.length > 0) {
            await overlayCanvasRef.current.loadPaths(paths);
        } else {
            await overlayCanvasRef.current.clearCanvas();
        }
    }, []);

    // Save reading progress
    const saveProgress = useCallback(async (pagina, totalPaginas) => {
        if (!activeItemAlumnoId) return;
        try {
            await axios.post(
                `${apiBase}api/cursosPdfAnotaciones/guardarProgreso`,
                {
                    itemalumnoid: activeItemAlumnoId,
                    ultima_pagina: pagina,
                    tiempo_lectura: 0, // could be tracked with a timer in the future
                    completado: pagina >= totalPaginas ? 1 : 0
                },
                { headers: getAuthHeader() }
            );
        } catch (err) {
            console.warn('PDF: no se pudo guardar el progreso', err);
        }
    }, [activeItemAlumnoId, apiBase]);
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const measureWrapper = () => {
            if (!wrapperRef.current) return;
            const { clientWidth, clientHeight } = wrapperRef.current;
            if (clientWidth > 10 && clientHeight > 10) {
                const h = clientHeight - 60; // leave space for bottom bar
                const w = Math.round(h * (550 / 712));
                setBookSize({ width: w, height: h });
            }
        };
        const timer = setTimeout(measureWrapper, 150);
        window.addEventListener('resize', measureWrapper);
        return () => { clearTimeout(timer); window.removeEventListener('resize', measureWrapper); };
    }, []);

    // Anti-screenshot: blur when window loses focus
    const [isWindowFocused, setIsWindowFocused] = useState(true);
    useEffect(() => {
        const onFocus = () => setIsWindowFocused(true);
        const onBlur = () => setIsWindowFocused(false);
        const onBefore = () => setIsWindowFocused(false);
        const onAfter = () => setIsWindowFocused(true);
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        window.addEventListener('beforeprint', onBefore);
        window.addEventListener('afterprint', onAfter);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('beforeprint', onBefore);
            window.removeEventListener('afterprint', onAfter);
        };
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(console.error);
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    function onDocumentLoadSuccess({ numPages }) { setNumPages(numPages); }

    const enablePen = () => {
        setIsDrawingMode(true);
        setIsHighlighter(false);
        setBrushRadius(3);
    };

    const enableHighlighter = () => {
        setIsDrawingMode(true);
        setIsHighlighter(true);
        setBrushRadius(18);
    };

    const undoDrawing = () => {
        if (overlayCanvasRef.current) overlayCanvasRef.current.undo();
    };

    const clearDrawing = () => {
        if (overlayCanvasRef.current) overlayCanvasRef.current.clearCanvas();
    };

    // Actual stroke color: highlighter uses rgba, pen uses hex
    const strokeColor = isHighlighter
        ? `${brushColor}55` // ~33% opacity for highlighter
        : brushColor;

    return (
        <div
            className={`pdf-viewer-container ${isDrawingMode ? 'drawing-mode-active' : ''}`}
            ref={containerRef}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Anti-screenshot overlay */}
            {!isWindowFocused && (
                <div className="anti-screenshot-overlay">
                    <h2>Contenido Protegido</h2>
                    <p>Las capturas de pantalla están deshabilitadas.</p>
                </div>
            )}

            {/* ── LEFT SIDEBAR ─────────────────────────────── */}
            <div className="drawing-sidebar">

                {/* Mode toggle */}
                <button
                    className={`tool-btn ${!isDrawingMode ? 'active' : ''}`}
                    onClick={() => setIsDrawingMode(false)}
                    title="Mover / Pasar Página"
                >✋</button>

                <div className="toolbar-divider-h" />

                {/* Pen */}
                <button
                    className={`tool-btn ${isDrawingMode && !isHighlighter ? 'active' : ''}`}
                    onClick={enablePen}
                    title="Lápiz"
                >✏️</button>

                {/* Highlighter */}
                <button
                    className={`tool-btn ${isDrawingMode && isHighlighter ? 'active' : ''}`}
                    onClick={enableHighlighter}
                    title="Marcador"
                >🖍️</button>

                {/* Color picker */}
                <label className="color-picker-label" title="Color del trazo">
                    <input
                        type="color"
                        value={brushColor}
                        onChange={(e) => { setBrushColor(e.target.value); enablePen(); }}
                        className="color-picker-input"
                    />
                    <span className="color-preview" style={{ background: brushColor }} />
                </label>

                {/* Stroke size */}
                <input
                    type="range"
                    min="1"
                    max="20"
                    value={brushRadius}
                    onChange={(e) => setBrushRadius(Number(e.target.value))}
                    className="size-slider"
                    title={`Tamaño: ${brushRadius}px`}
                    orient="vertical"
                />

                <div className="toolbar-divider-h" />

                {/* Undo / Clear */}
                <button className="tool-btn text-warning" onClick={undoDrawing} title="Deshacer">↩️</button>
                <button className="tool-btn text-danger" onClick={clearDrawing} title="Borrar Todo">🗑️</button>

                <div className="toolbar-divider-h" />

                {/* Zoom */}
                <button className="tool-btn" onClick={() => setScale(s => Math.min(+(s + 0.1).toFixed(1), 3))} title="Acercar">🔍+</button>
                <span className="zoom-label">{Math.round(scale * 100)}%</span>
                <button className="tool-btn" onClick={() => setScale(s => Math.max(+(s - 0.1).toFixed(1), 0.4))} title="Alejar">🔍−</button>
                <button className="tool-btn" onClick={() => setScale(1)} title="100%">⊙</button>

                <div className="toolbar-divider-h" />

                {/* Fullscreen */}
                <button className="tool-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}>
                    {isFullscreen ? '⛶' : '⛶'}⛶
                </button>
            </div>

            {/* ── BOOK AREA ────────────────────────────────── */}
            <div className="flipbook-wrapper" ref={wrapperRef}>
                {pdfError && <div style={{ color: 'red', background: 'white', padding: 10 }}>{pdfError}</div>}

                <Document
                    file={activePdf}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(e) => setPdfError('Error: ' + e.message)}
                    onSourceError={(e) => setPdfError('Error: ' + e.message)}
                    loading={<div className="loading-pdf">Cargando documento...</div>}
                    error={<div className="error-pdf">Error al cargar. {pdfError}</div>}
                >
                    {numPages && (
                        <div
                            className="book-scale-wrapper"
                            style={{ transform: `scale(${scale})`, transformOrigin: 'center top' }}
                        >
                            <HTMLFlipBook
                                width={bookSize.width}
                                height={bookSize.height}
                                size="fixed"
                                maxShadowOpacity={0.5}
                                showCover={true}
                                useMouseEvents={!isDrawingMode}
                                mobileScrollSupport={!isDrawingMode}
                                ref={flipBookRef}
                                className="flipbook-component"
                                startPage={initialPage}
                                onFlip={async (e) => {
                                    const newPage = e.data + 1;
                                    // Save current page before leaving
                                    await saveAnnotationsForPage(pageNumber);
                                    await saveProgress(newPage, numPages);
                                    setPageNumber(newPage);
                                    // Load annotations for the new page
                                    setTimeout(() => loadAnnotationsForPage(newPage), 100);
                                }}
                                swipeDistance={isDrawingMode ? 99999 : 30}
                            >
                                {Array.from(new Array(numPages), (el, index) => (
                                    <PageComponent
                                        key={`page_${index + 1}`}
                                        pageNumber={index + 1}
                                        width={bookSize.width}
                                    />
                                ))}
                            </HTMLFlipBook>

                            {/* Drawing overlay — sits perfectly on top of the book, outside react-pageflip DOM */}
                            {isDrawingMode && (
                                <div
                                    className="overlay-canvas-wrapper"
                                    style={{ width: bookSize.width, height: bookSize.height }}
                                >
                                    <ReactSketchCanvas
                                        ref={overlayCanvasRef}
                                        strokeColor={strokeColor}
                                        strokeWidth={brushRadius}
                                        width={bookSize.width}
                                        height={bookSize.height}
                                        canvasColor="transparent"
                                        style={{ background: 'transparent', border: 'none' }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Document>

                {numPages && (
                    <div className="pdf-controls">
                        Página {pageNumber} de {numPages}
                        <button onClick={toggleFullscreen} className="fullscreen-btn">
                            {isFullscreen ? 'Salir Pantalla Completa' : 'Pantalla Completa'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
